import React from 'react';
import PropTypes from 'prop-types';

/**
 * Button Component - Design System
 * 
 * A reusable button component with multiple variants, sizes, and states.
 * Follows the dark theme design system with consistent styling and accessibility.
 * 
 * @component
 * @example
 * // Primary button
 * <Button variant="primary" size="lg" onClick={handleClick}>
 *   Shop Now
 * </Button>
 * 
 * // Secondary button with icon
 * <Button variant="secondary" icon={<CartIcon />}>
 *   Add to Cart
 * </Button>
 */
const Button = ({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  icon = null,
  children,
  onClick,
  type = 'button',
  className = '',
  ...props
}) => {
  // Base styles - applied to all buttons (dual-theme support)
  const baseStyles = `
    inline-flex items-center justify-center
    font-semibold rounded-button
    transition-all duration-slow ease-in-out
    focus:outline-none focus:ring-2 focus:ring-offset-2 
    focus:ring-offset-light-primary dark:focus:ring-offset-brand-dark
    disabled:opacity-50 disabled:cursor-not-allowed
    active:scale-[0.98] active:duration-150
    ${fullWidth ? 'w-full' : ''}
  `;

  // Variant styles with dual-theme support
  const variantStyles = {
    primary: `
      bg-black hover:bg-gray-900
      dark:bg-black dark:hover:bg-gray-900
      text-white dark:text-white
      focus:ring-black dark:focus:ring-black
      disabled:hover:bg-black dark:disabled:hover:bg-black
    `,
    secondary: `
      bg-transparent hover:bg-black/10
      dark:hover:bg-white/10
      border-2 border-black dark:border-white
      text-black dark:text-white
      focus:ring-black dark:focus:ring-white
    `,
    ghost: `
      bg-transparent 
      hover:bg-light-elevated dark:hover:bg-brand-elevated
      text-light-text-primary dark:text-brand-text-primary
      focus:ring-light-text-primary dark:focus:ring-brand-text-primary
    `,
    accent: `
      bg-light-accent-red hover:bg-light-accent-redHover
      dark:bg-brand-accent-red dark:hover:bg-brand-accent-redHover
      text-white dark:text-white
      focus:ring-light-accent-red dark:focus:ring-brand-accent-red
      disabled:hover:bg-light-accent-red dark:disabled:hover:bg-brand-accent-red
    `,
  };

  // Size styles
  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };

  // Loading spinner component
  const LoadingSpinner = () => (
    <svg
      className="animate-spin -ml-1 mr-2 h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        ${baseStyles}
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      {...props}
    >
      {loading && <LoadingSpinner />}
      {icon && !loading && <span className="mr-2">{icon}</span>}
      {children}
    </button>
  );
};

Button.propTypes = {
  /** Button variant style */
  variant: PropTypes.oneOf(['primary', 'secondary', 'ghost', 'accent']),
  /** Button size */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Loading state - shows spinner */
  loading: PropTypes.bool,
  /** Full width button */
  fullWidth: PropTypes.bool,
  /** Icon element to display before text */
  icon: PropTypes.node,
  /** Button content */
  children: PropTypes.node.isRequired,
  /** Click handler */
  onClick: PropTypes.func,
  /** Button type attribute */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default Button;
